/**
 * authStore — session + user state, sourced from Supabase Auth.
 *
 * Only UI/session state lives here. Server state (profiles, tasks, etc.)
 * goes through React Query, never Zustand. See CLAUDE.md Section 3.
 */

import { create }                from 'zustand'
import type { Session, User }    from '@supabase/supabase-js'
import { isValidRole, type Role } from '@/types/roles'

interface AuthState {
  session:    Session | null
  user:       User | null
  /** True until the first onAuthStateChange resolves. Routes wait on this. */
  loading:    boolean
  setSession: (session: Session | null) => void
  setLoading: (loading: boolean) => void
  reset:      () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user:    null,
  loading: true,
  setSession: (session) => set({
    session,
    user:    session?.user ?? null,
    loading: false,
  }),
  setLoading: (loading) => set({ loading }),
  reset: () => set({ session: null, user: null, loading: false }),
}))

/** Reads the role from JWT app_metadata. Returns null for unauthenticated or unknown role. */
export function selectRole(state: AuthState): Role | null {
  const raw = state.user?.app_metadata?.role
  return isValidRole(raw) ? raw : null
}

/** Reads organization_id from JWT app_metadata. */
export function selectOrgId(state: AuthState): string | null {
  const raw = state.user?.app_metadata?.org_id
  return typeof raw === 'string' ? raw : null
}

/** Reads the auth.users.id of the currently-signed-in user. */
export function selectUserId(state: AuthState): string | null {
  return state.user?.id ?? null
}
